<template>
  <div>
    <mia-wait for="loading files">
      <template #waiting>
        <div class="flex items-center justify-center py-8">
          <mia-wave-loader />
        </div>
      </template>

      <template #default>
        <template v-if="filteredFiles.length === 0">
          <slot name="no-files">
            Es wurden noch keine Dateien hinzugefügt
          </slot>
        </template>
        <template v-else>
          <datatable
            ref="dataTable"
            :columns="filesColumns"
            :data="filteredFiles"
            class="sticky w-full table-auto datatable dragula-table"
          />
        </template>

        <div
          v-if="!readonly"
          class="p-4 border-t border-gray-300"
        >
          <mia-add-action class="w-full">
            <label>
              <span class="tracking-wider text-gray-800">Dateien hinzufügen:</span>
              <input
                v-if="!fileUploadIsLoading"
                type="file"
                :accept="accept"
                multiple
                class="mt-1 bg-white"
                @change="fileUpload($event.target.files, privacy ? {'privacy_file' : true} : {})"
              >
            </label>
          </mia-add-action>
        </div>

        <div
          v-if="fileUploadIsLoading"
          class="p-8 text-white rounded bg-orange-mia"
        >
          Bitte warten, Dateien werden hinzugefügt...
        </div>
      </template>
    </mia-wait>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import orderBy from 'lodash/orderBy'
import dragula from 'dragula'
import 'dragula/dist/dragula.css'
import { sortFiles } from '@/api/cars'
import DatatableInlineEditColumn from '@/components/general/DatatableInlineEditColumn.vue'
import DatatableRemove from '@/components/general/DatatableRemove.vue'
import CustomerDatatableAttachment from '@/components/general/CustomerDatatableAttachment.vue'
import DragAndDropSortableColumn from '@/components/ui/datatable/columns/DragAndDropSortableColumn.vue'
import AddAction from '@/components/ui/AddAction.vue'
import { mapGetters } from 'vuex'
import { deleteFile, getFiles, updateFiles } from '@/api/customer'

export default {
  components: {
    'mia-add-action': AddAction
  },

  props: {
    accept: {
      type: String,
      default: 'image/*,.pdf'
    },

    only: {
      type: Array,
      default() {
        return []
      }
    },

    columns: {
      type: Array,
      default() {
        return ['preview', 'filename', 'actions']
      }
    },

    sortable: {
      type: Boolean,
      default: false
    },

    privacy: {
      type: Boolean,
      default: false
    },

    readonly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      files: [],
      fileUploadIsLoading: false,
      uploadProgress: null,
      uploadingCategory: null
    }
  },

  computed: {
    ...mapGetters('customers', ['customer']),

    filesColumns() {
      const columns = []

      if (this.sortable) {
        columns.push({
          label: 'Sortieren',
          field: 'sort',
          headerAlign: 'left',
          component: DragAndDropSortableColumn,
          sortable: false
        })
      }

      if (this.columns.includes('preview')) {
        columns.push({
          label: 'Vorschau & Download',
          field: 'key',
          headerAlign: 'left',
          component: CustomerDatatableAttachment,
          meta: {
            downloadUrl: file => `${process.env.VUE_APP_PROVIDER}/customers/${this.customer.id}/getfile/${file.id}`,
            previewUrl: file => `${process.env.VUE_APP_PROVIDER}/customers/${this.customer.id}/getfilepreview/${file.id}`
          }
        })
      }

      if (this.columns.includes('filename')) {
        if (this.readonly) {
          columns.push({
            label: 'Dateiname',
            field: 'item_filename',
            headerAlign: 'left',
            class: 'w-1/2'
          })
        } else {
          columns.push({
            label: 'Dateiname',
            field: 'item_filename',
            headerAlign: 'left',
            component: DatatableInlineEditColumn,
            class: 'w-1/2',
            meta: {
              listeners: {
                'keydown.enter': (_, event) => {
                  event.target.blur()
                },
                blur: row => this.updateFile(row)
              }
            }
          })
        }
      }

      if (this.columns.includes('actions') && !this.readonly) {
        columns.push({
          label: 'Löschen',
          headerAlign: 'center',
          component: DatatableRemove,
          class: 'w-1/8 text-center',
          meta: {
            confirm: true,
            removeInline: false,
            onRemove: (file, setLoading, done) => this.onRemove(file, setLoading, done)
          }
        })
      }

      return columns
    },

    filteredFiles() {
      const filteredFiles = this.files
        .filter((file) => {
          if (this.privacy && file.item_category !== 'customer_privacy_statement') return false
          if (this.only.length === 0) return true

          return this.only.includes(file.item_filetype)
        })
        .map(file => ({
          ...file,
          id: `${file.id}`
        }))

      return orderBy(filteredFiles, 'item_sort')
    }
  },

  watch: {
    'customer.id': function () {
      this.loadFiles()
    }
  },

  mounted() {
    this.loadFiles()
  },

  beforeDestroy() {
    this.destroySorting()
  },

  methods: {
    async loadFiles() {
      this.files = []

      this.$wait.start('loading files')
      const [error, files] = await getFiles(this.customer.id)
      this.$wait.end('loading files')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Dateien konnte nicht abgerufen werden']
        })
        return
      }

      this.files = files

      this.$emit('update:files', files)

      if (this.sortable) {
        this.$nextTick(() => {
          this.initSorting()
        })
      }
    },

    fileUpload(files, additionalFields = {}) {
      this.fileUploadIsLoading = true
      const formData = new FormData()

      Object.entries(files).forEach(([key, value]) => {
        formData.set(key, value)
      })

      formData.set('customerId', this.customer.id)

      Object.entries(additionalFields).forEach(([field, value]) => {
        formData.set(field, value)
      })

      axios.post(`${window.baseUrl}/customeredit/uploadfiles`, formData, {
        headers: {
          'content-type': 'multipart/form-data'
        },
        onUploadProgress(event) {
          this.uploadProgress = Math.round((event.loaded * 100) / event.total)
        }
      })
        .then(({ data }) => {
          Object.values(data.item_files).forEach((file) => {
            this.files.push(file)
          })

          this.$emit('update:files', this.files)

          window.eventBus.$emit('mia-info', {
            info: ['Datei(en) erfolgreich hochgeladen.']
          })

          if (data.item_duplicates && data.item_duplicates.length > 0) {
            window.eventBus.$emit('mia-info', {
              info: ['Duplikate wurden nicht importiert.']
            })
          }
        }).finally(() => {
          this.fileUploadIsLoading = false
          this.uploadProgress = null
          this.uploadingCategory = null
        })
    },

    // eslint-disable-next-line prefer-arrow-callback
    updateFile: debounce(async function (row) {
      const updatedFiles = [...this.files].map((file) => {
        if (parseInt(file.id, 10) === parseInt(row.id, 10)) {
          return {
            ...file,
            ...row
          }
        }

        return file
      })

      try {
        await updateFiles(this.customer.id, updatedFiles)

        window.eventBus.$emit('mia-info', {
          info: ['Änderungen erfolgreich gespeichert.']
        })
      } catch (e) {
        console.log(e)

        window.eventBus.$emit('mia-error', {
          error: ['Änderungen konnten nicht gespeichert werden.']
        })
      }
    }, 750),

    initSorting() {
      if (!this.$refs.dataTable) {
        return
      }

      const draggableContainer = this.$refs.dataTable.$el.querySelector('tbody')

      this.dragulaInstance = dragula([draggableContainer], {
        direction: 'vertical',
        revertOnSpill: true,
        invalid(_, handle) {
          if (handle.classList.contains('dragula-handle')) {
            return false
          }

          let parent = handle.parentNode

          while (parent) {
            if (!parent.classList) {
              return true
            }

            if (parent.classList.contains('dragula-handle')) {
              return false
            }

            parent = parent.parentNode
          }

          return true
        }
      })
        .on('drag', () => {
          document.body.classList.add('dragula-dragging')
        })
        .on('dragend', () => {
          document.body.classList.remove('dragula-dragging')

          this.saveCurrentSorting()
        })
    },

    destroySorting() {
      if (this.dragulaInstance) {
        this.dragulaInstance.destroy()
      }
    },

    async saveCurrentSorting() {
      const sorting = {}

      this.$refs.dataTable.$el.querySelectorAll('tbody > tr').forEach((row, index) => {
        const { id } = row.querySelector('[data-id]').dataset

        sorting[id] = index
      })

      const [error] = await sortFiles(sorting)

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Sortierung konnte nicht gespeichert werden.']
        })

        return
      }

      window.eventBus.$emit('mia-info', {
        info: ['Sortierung erfolgreich gespeichert.']
      })
    },

    async onRemove(file, setLoading, done) {
      if (!file.id) return

      setLoading(true)
      const [error] = await deleteFile(this.customer.id, file.id)
      setLoading(false)

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Datei konnte nicht gelöscht werden.']
        })

        return
      }

      this.files = this.files.filter(f => parseInt(f.id, 10) !== parseInt(file.id, 10))

      this.$emit('update:files', this.files)

      window.eventBus.$emit('mia-info', {
        info: ['Datei erfolgreich gelöscht']
      })

      done()
    }
  }
}
</script>


<style lang="scss" scoped>
  label {
    @apply font-normal;
    @apply w-full;
  }
</style>

<style lang="scss">
body.dragula-dragging {
  &,
  & * {
    cursor: grabbing !important;
  }
}

.dragula-table {
  tbody > tr {
    td {
      transform: translateZ(0);
      -webkit-backface-visibility: hidden;
      -webkit-perspective: 1000;
    }
  }
}
</style>
